'use client';

import { createPortal } from 'react-dom';
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import useAuthStore from '@/store/useAuthStore';
import { apiClient } from '@/lib/api-client';
import NormalButton from '@/components/Button/normal';

const SUBJECT_LABELS: Record<string, string> = {
  math: 'Math',
  science: 'Science',
  history: 'Social Studies',
  vocabulary: 'Vocabulary',
  grammar: 'Grammar',
  'amc-math': 'AMC Math',
};

function normalizeSubject(value: string | null | undefined) {
  return String(value || '').trim().toLowerCase().replace(/_/g, '-');
}

interface ConfirmSubjectSelectionModalProps {
  visible: boolean;
  subject: string | null;
  currentSubject?: string | null;
  onClose: () => void;
  onConfirmed?: () => void | Promise<void>;
  onViewDetails?: () => void;
}

export default function ConfirmSubjectSelectionModal({
  visible,
  subject,
  currentSubject = null,
  onClose,
  onConfirmed,
  onViewDetails,
}: ConfirmSubjectSelectionModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const normalizedSubject = normalizeSubject(subject);
  const normalizedCurrentSubject = normalizeSubject(currentSubject);

  const subjectLabel = useMemo(
    () => SUBJECT_LABELS[normalizedSubject] ?? normalizedSubject,
    [normalizedSubject],
  );
  const currentSubjectLabel = useMemo(
    () => SUBJECT_LABELS[normalizedCurrentSubject] ?? normalizedCurrentSubject,
    [normalizedCurrentSubject],
  );

  if (!visible || !normalizedSubject || typeof window === 'undefined') {
    return null;
  }

  const handleConfirm = async () => {
    setSaving(true);
    setError('');

    try {
      const res = await apiClient.patch('/api/profile/subject', { subject: normalizedSubject });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(data?.error || 'Failed to update subject');
        return;
      }

      useAuthStore.setState((state) => ({
        profile: state.profile
          ? {
              ...state.profile,
              free_subject: normalizedSubject,
              subject_changed_at: new Date().toISOString(),
            }
          : null,
      }));

      await onConfirmed?.();
      onClose();
    } catch {
      setError('Failed to update subject');
    } finally {
      setSaving(false);
    }
  };

  const isSwitching = Boolean(normalizedCurrentSubject && normalizedCurrentSubject !== normalizedSubject);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
      style={{ zIndex: 1350 }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[430px] rounded-[24px] bg-white px-5 pb-5 pt-5 shadow-[0_20px_56px_rgba(15,23,42,0.18)] animate-fade-in sm:px-6 sm:pb-6"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-[#667085] transition-colors hover:bg-[#F3F4F6] hover:text-[#344054]"
          aria-label="Close subject confirmation"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="pr-10 pt-2">
          <h3 className="display-text-bold whitespace-nowrap text-[1.05rem] leading-tight text-[#1A1C1E] sm:text-[1.15rem]">
            {isSwitching
              ? `Switch to ${subjectLabel} for the next 7 days?`
              : `Make ${subjectLabel} your subject for the next 7 days?`}
          </h3>
          <p className="display-text mt-3 text-[13px] leading-6 text-[#6B7280] sm:text-[14px]">
            {isSwitching
              ? `This will replace ${currentSubjectLabel} as your current subject.`
              : 'Free users learn one subject at a time. You can switch again in 7 days.'}
          </p>
        </div>

        {error ? <p className="display-text mt-3 text-[12px] text-red-500">{error}</p> : null}

        <div className="mt-5 flex flex-col gap-2.5">
          <NormalButton
            onClick={() => void handleConfirm()}
            colorType="green"
            className="h-11 w-full rounded-full"
            disabled={saving}
          >
            <span className="text-[12px] sm:text-[13px]">{saving ? 'Saving...' : 'Confirm'}</span>
          </NormalButton>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <NormalButton
              onClick={onClose}
              colorType="white"
              className="h-11 w-full rounded-full"
              disabled={saving}
            >
              <span className="text-[12px] sm:text-[13px]">Cancel</span>
            </NormalButton>
            {onViewDetails ? (
              <NormalButton
                onClick={onViewDetails}
                colorType="white"
                className="h-11 w-full rounded-full"
                disabled={saving}
              >
                <span className="text-[12px] sm:text-[13px]">View details</span>
              </NormalButton>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
